import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { User, Star, ShieldCheck, MapPin } from '@phosphor-icons/react';
import Navigation from '@/components/Navigation';
import { API } from '@/App';
import { toast } from 'sonner';

const Profile = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await fetch(`${API}/users/${userId}`, { credentials: 'include' });
        const userData = await userRes.json();
        setUser(userData);

        const reviewsRes = await fetch(`${API}/reviews/${userId}`, { credentials: 'include' });
        const reviewsData = await reviewsRes.json();
        setReviews(reviewsData);

        setLoading(false);
      } catch (error) {
        toast.error('Failed to load profile');
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D28D9]"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">User not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Navigation />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl border border-gray-200 p-8 mb-8">
          <div className="flex items-start gap-6">
            {user.profile_image ? (
              <img
                src={user.profile_image}
                alt={user.name}
                className="w-24 h-24 rounded-full object-cover"
                data-testid="profile-image"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#1E2A45] flex items-center justify-center">
                <User size={48} weight="duotone" className="text-gray-600" />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1
                  className="text-3xl font-extrabold text-gray-900"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                  data-testid="profile-name"
                >
                  {user.name}
                </h1>
                {user.verification_status === 'verified' && (
                  <ShieldCheck size={28} weight="fill" className="text-[#10B981]" data-testid="verified-badge" />
                )}
              </div>
              <p className="text-gray-600 font-medium capitalize mb-3">{user.user_type || 'User'}</p>
              {user.rating > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <Star size={24} weight="fill" className="text-[#F59E0B]" />
                  <span className="text-2xl font-bold text-gray-900">{user.rating.toFixed(1)}</span>
                  <span className="text-gray-500">({reviews.length} reviews)</span>
                </div>
              )}
              {user.country && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin size={20} weight="bold" className="text-[#6D28D9]" />
                  <span>{user.country}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <h2
            className="text-2xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="reviews-heading"
          >
            Reviews
          </h2>
          {reviews.length === 0 ? (
            <div className="text-center py-12" data-testid="no-reviews">
              <Star size={48} weight="duotone" className="text-gray-500 mx-auto mb-4" />
              <p className="text-gray-600">No reviews yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div key={review.review_id} className="pb-6 border-b border-gray-200 last:border-b-0" data-testid={`review-${review.review_id}`}>
                  <div className="flex items-start gap-4">
                    {review.reviewer_image ? (
                      <img
                        src={review.reviewer_image}
                        alt={review.reviewer_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#1E2A45] flex items-center justify-center">
                        <span className="text-gray-600 font-bold">
                          {review.reviewer_name?.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-bold text-gray-900">{review.reviewer_name}</p>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star
                              key={i}
                              size={16}
                              weight={i <= review.rating ? 'fill' : 'regular'}
                              className={i <= review.rating ? 'text-[#F59E0B]' : 'text-gray-300'}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{review.comment}</p>
                      <p className="text-sm text-gray-500 mt-2">
                        {new Date(review.created_at).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
